// Hook que carga el customer ligado al user actual + su tier.
// Para uso en /portal — el guard `RequirePortal` valida que existe y está activo.

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth';

export type CustomerWithTier = {
  id: string;
  customer_type: string;
  business_name: string;
  legal_name: string | null;
  rfc: string | null;
  regimen_fiscal: string | null;
  cfdi_use: string | null;
  primary_email: string | null;
  primary_phone: string | null;
  whatsapp: string | null;
  status: string;
  credit_limit_mxn: number | null;
  credit_days: number | null;
  credit_balance_mxn: number | null;
  total_orders: number;
  total_revenue_mxn: number;
  lutron_lines_certified: string[];
  default_markup_pct: number | null;
  tier_id: string | null;
  tier_code: string | null;
  tier_name: string | null;
  tier_discount_pct: number | null;
  // Descuento por marca: { nuled: 25, lutron: 18 }. Si la marca no está, fallback a tier_discount_pct.
  discount_by_brand: Record<string, number>;
  membership_role: 'admin' | 'buyer' | 'viewer';
};

// Helper: descuento que aplica a un producto según su brand_code
export function discountForBrand(customer: CustomerWithTier | null, brandCode: string | null | undefined): number {
  if (!customer) return 0;
  const key = (brandCode || '').toLowerCase();
  if (key && customer.discount_by_brand[key] != null) {
    return customer.discount_by_brand[key];
  }
  return customer.tier_discount_pct ?? 0;
}

type Ctx = {
  customer: CustomerWithTier | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

const CustomerCtx = createContext<Ctx | null>(null);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [customer, setCustomer] = useState<CustomerWithTier | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!session?.user) { setCustomer(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('customer_users')
      .select(`
        role,
        customer:customers (
          id, customer_type, business_name, legal_name,
          rfc, regimen_fiscal, cfdi_use,
          primary_email, primary_phone, whatsapp,
          status, credit_limit_mxn, credit_days, credit_balance_mxn,
          total_orders, total_revenue_mxn,
          lutron_lines_certified, default_markup_pct,
          tier:customer_tiers ( id, code, name, default_discount_pct )
        )
      `)
      .eq('user_id', session.user.id)
      .maybeSingle();
    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    if (!data || !data.customer) {
      setCustomer(null);
      setLoading(false);
      return;
    }
    const c = data.customer as any;
    const t = c.tier as any;

    // Cargar descuentos por marca del cliente
    const discBrand: Record<string, number> = {};
    const { data: cdb } = await supabase
      .from('customer_discount_brand')
      .select('brand_code, discount_pct')
      .eq('customer_id', c.id);
    for (const row of (cdb ?? [])) {
      discBrand[(row as any).brand_code] = Number((row as any).discount_pct);
    }

    setCustomer({
      id: c.id,
      customer_type: c.customer_type,
      business_name: c.business_name,
      legal_name: c.legal_name,
      rfc: c.rfc,
      regimen_fiscal: c.regimen_fiscal,
      cfdi_use: c.cfdi_use,
      primary_email: c.primary_email,
      primary_phone: c.primary_phone,
      whatsapp: c.whatsapp,
      status: c.status,
      credit_limit_mxn: c.credit_limit_mxn ? Number(c.credit_limit_mxn) : null,
      credit_days: c.credit_days,
      credit_balance_mxn: c.credit_balance_mxn ? Number(c.credit_balance_mxn) : null,
      total_orders: c.total_orders ?? 0,
      total_revenue_mxn: c.total_revenue_mxn ? Number(c.total_revenue_mxn) : 0,
      lutron_lines_certified: c.lutron_lines_certified ?? [],
      default_markup_pct: c.default_markup_pct ? Number(c.default_markup_pct) : null,
      tier_id: t?.id ?? null,
      tier_code: t?.code ?? null,
      tier_name: t?.name ?? null,
      tier_discount_pct: t?.default_discount_pct ? Number(t.default_discount_pct) : null,
      discount_by_brand: discBrand,
      membership_role: data.role as 'admin' | 'buyer' | 'viewer',
    });
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [session?.user?.id]);

  return (
    <CustomerCtx.Provider value={{ customer, loading, error, reload: load }}>
      {children}
    </CustomerCtx.Provider>
  );
}

export function useCustomer() {
  const v = useContext(CustomerCtx);
  if (!v) throw new Error('useCustomer fuera de CustomerProvider');
  return v;
}
